import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Users, Calendar, Search, Plus, MoreHorizontal, GitBranch, Trash2, Gamepad2, Edit3 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTeamsStore } from '../store/useTeamsStore'
import { useProjectsStore } from '../store/useProjectsStore'
import { teamsService } from '../services/teamsService'
import authService from '../services/authService'
import StatusBadge from '../components/common/StatusBadge'
import SmoothLoadingSpinner from '../components/common/SmoothLoadingSpinner'
import TeamMembersList from '../components/teams/TeamMembersList'
import TeamEditModal from '../components/teams/TeamEditModal'
import AddGameModal from '../components/teams/AddGameModal'
import { FILL_GRADIENT, LINE_GRADIENT } from '../constants/gradients'
import { analyticsService } from '../services/analyticsService'

// Helper function to format time
const formatTimeAgo = (dateString: string) => {
  if (!dateString) return 'Unknown date'
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return 'Invalid date'
  
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  
  if (diffInSeconds < 60) return 'Just now'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`
  return date.toLocaleDateString()
}

const TeamDetailPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>()
  const navigate = useNavigate()
  const { teams, personalWorkspace, loading, refreshTeams, refreshPersonalWorkspace } = useTeamsStore()
  const { projects, fetchProjects, refreshProjects, isLoadingProjects } = useProjectsStore()
  
  const [activeTab, setActiveTab] = useState<'projects' | 'members'>('projects')
  const [searchQuery, setSearchQuery] = useState('')
  const [showActionsMenu, setShowActionsMenu] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAddGameModal, setShowAddGameModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [currentUser, setCurrentUser] = useState<any>(null)

  const isPersonalWorkspace = teamId === 'personal'
  const team = isPersonalWorkspace ? personalWorkspace : teams.find(t => t.id === teamId)

  useEffect(() => {
    if (isPersonalWorkspace) {
      refreshPersonalWorkspace()
    } else {
      refreshTeams()
    }
    fetchProjects()
    loadCurrentUser()
    
    // Track team viewed
    if (teamId && !isPersonalWorkspace) {
      analyticsService.track({
        name: 'team_viewed',
        properties: {
          team_id: teamId
        }
      });
    }
  }, [teamId, isPersonalWorkspace])

  const loadCurrentUser = async () => {
    try {
      const userData = await authService.getCurrentUser()
      setCurrentUser(userData)
    } catch (error) {
      console.error('Failed to load current user:', error)
    }
  }

  const handleDeleteTeam = async () => {
    if (!teamId || isPersonalWorkspace) return
    
    setIsDeleting(true)
    try {
      // Track team deletion
      analyticsService.track({
        name: 'team_deleted',
        properties: {
          team_id: teamId,
          team_name: team?.name
        }
      });
      
      await teamsService.deleteTeam(teamId)
      await refreshTeams()
      toast.success('Team deleted successfully')
      navigate('/teams')
    } catch (error) {
      console.error('Failed to delete team:', error)
      toast.error('Failed to delete team')
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const handleEditTeamSave = async () => {
    await refreshTeams()
    setShowEditModal(false)
  }

  if (loading.teams || loading.personalWorkspace || !team) {
    return (
      <div className="p-6">
        <SmoothLoadingSpinner 
          isLoading={true} 
          size="lg" 
          message="Loading team details..." 
          className="py-12" 
        />
      </div>
    )
  }

  const handleTabChange = (tab: 'projects' | 'members') => {
    setActiveTab(tab)
  }

  const filteredProjects = projects.filter(project => {
    if (isPersonalWorkspace) {
      // Check multiple conditions for personal projects
      const isPersonalProject = !project.team || 
                                project.team?.is_personal === true ||
                                project.team?.name === 'Personal' || 
                                project.team_id === null || 
                                project.team_id === undefined ||
                                project.ownership_type === 'personal' ||
                                project.user_role === 'owner';
      
      return isPersonalProject;
    }
    return project.team?.id === teamId
  })

  const searchedProjects = searchQuery.trim() 
    ? filteredProjects.filter(project => 
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (project.description && project.description.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : filteredProjects

  const renderHeader = () => (
    <div className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border">
      <div className="max-w-7xl mx-auto p-8">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/teams')}
            className="flex items-center gap-2 text-gray-500 dark:text-dark-text-muted hover:text-gray-900 dark:hover:text-dark-text transition-colors font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Teams
          </button>
          
          <div className="relative">
            <button
              onClick={() => setShowActionsMenu(!showActionsMenu)}
              className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <MoreHorizontal className="w-5 h-5" />
            </button>
            
            {showActionsMenu && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl shadow-lg z-10">
                <div className="py-2">
                  {!isPersonalWorkspace && (
                    <>
                      <button 
                        onClick={() => {
                          setShowEditModal(true)
                          setShowActionsMenu(false)
                        }}
                        className="w-full text-left px-4 py-2 text-sm font-semibold text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors flex items-center gap-2"
                      >
                        <Edit3 className="w-4 h-4" />
                        Edit Team
                      </button>
                      <button 
                        onClick={() => {
                          setShowDeleteConfirm(true)
                          setShowActionsMenu(false)
                        }}
                        className="w-full text-left px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Team
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-6">
          {/* Team Avatar */}
          <div className="relative">
            <div 
              className="w-24 h-24 rounded-full flex items-center justify-center overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #FCB902 0%, #FF00CF 100%)' }}
            >
              {(team as any).avatar_url || (team as any).avatarUrl ? (
                <img 
                  src={(team as any).avatar_url || (team as any).avatarUrl} 
                  alt={`${team.name} avatar`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Users className="w-12 h-12 text-white" />
              )}
            </div>
          </div>

          {/* Team Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-semibold text-gray-900 dark:text-dark-text">
                {isPersonalWorkspace ? 'Personal Workspace' : team.name}
              </h1>
              {isPersonalWorkspace && (
                <span className="px-3 py-1 rounded-md text-xs font-semibold uppercase bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 dark:from-blue-900/30 dark:to-purple-900/30 dark:text-blue-300">
                  personal
                </span>
              )}
              {!isPersonalWorkspace && 'isPublic' in team && team.isPublic && (
                <span className="px-3 py-1 rounded-md text-xs font-semibold uppercase bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                  public
                </span>
              )}
            </div>
            
            <p className="text-gray-500 dark:text-dark-text-muted mb-4">
              {isPersonalWorkspace 
                ? 'Your personal projects and workspace' 
                : (team as any).description || 'No description provided'
              }
            </p>
            
            {(() => {
              // Robust fallbacks for stats: handle server snake_case and compute from local data
              const personalProjects = (team as any).totalProjects
              const teamProjectCount = isPersonalWorkspace
                ? (typeof personalProjects === 'number' ? personalProjects : filteredProjects.length)
                : (
                  (team as any).projectCount ??
                  (team as any).game_count ??
                  ((team as any).projects?.length) ??
                  filteredProjects.length ??
                  0
                )
              
              const teamMemberCount = !isPersonalWorkspace
                ? (
                  (team as any).memberCount ??
                  (team as any).member_count ??
                  ((team as any).members?.length) ??
                  0
                )
                : null
              
              const computedLatestProjectDate = (() => {
                const dates = filteredProjects
                  .map(p => (p as any).updated_at || (p as any).created_at)
                  .filter(Boolean)
                  .map((d: string) => new Date(d))
                  .filter(d => !isNaN(d.getTime()))
                if (dates.length === 0) return null
                const latest = new Date(Math.max(...dates.map(d => d.getTime())))
                return latest.toISOString()
              })()
              
              const teamDateField =
                (team as any).lastActivity ||
                (team as any).last_activity ||
                (team as any).updatedAt ||
                (team as any).updated_at ||
                (team as any).createdAt ||
                (team as any).created_at ||
                null
              
              const lastActivityISO = !isPersonalWorkspace
                ? (computedLatestProjectDate || (teamDateField ? new Date(teamDateField).toISOString() : null))
                : null
              
              return (
                <div className="flex items-center gap-6 text-sm text-gray-500 dark:text-dark-text-muted">
                  <div className="flex items-center gap-1.5">
                    <Gamepad2 className="w-4 h-4" />
                    <span className="font-medium">
                      {teamProjectCount} {teamProjectCount === 1 ? 'game' : 'games'}
                    </span>
                  </div>
                  {!isPersonalWorkspace && (
                    <div className="flex items-center gap-1.5">
                      <Users className="w-4 h-4" />
                      <span className="font-medium">{teamMemberCount} {teamMemberCount === 1 ? 'member' : 'members'}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    <span className="font-medium">
                      {isPersonalWorkspace 
                        ? 'Always accessible' 
                        : (lastActivityISO ? formatTimeAgo(lastActivityISO) : 'No activity')
                      }
                    </span>
                  </div>
                </div>
              )
            })()}
          </div>
        </div>
      </div>
    </div>
  )

  const renderTabs = () => (
    <div className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border">
      <div className="max-w-7xl mx-auto px-8">
        <div className="flex gap-8">
          <button
            onClick={() => handleTabChange('projects')}
            className={`py-4 px-1 font-semibold text-sm transition-all duration-200 relative ${
              activeTab === 'projects'
                ? 'text-gray-900 dark:text-dark-text'
                : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text'
            }`}
          >
            Games
            {activeTab === 'projects' && (
              <div 
                className="absolute bottom-0 left-0 right-0 h-0.5"
                style={{ background: LINE_GRADIENT }}
              />
            )}
          </button>
          
          {!isPersonalWorkspace && (
            <button
              onClick={() => handleTabChange('members')}
              className={`py-4 px-1 font-semibold text-sm transition-all duration-200 relative ${
                activeTab === 'members'
                  ? 'text-gray-900 dark:text-dark-text'
                  : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text'
              }`}
            >
              Members
              {activeTab === 'members' && (
                <div 
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{ background: LINE_GRADIENT }}
                />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )

  const renderProjectsTab = () => (
    <div className="max-w-7xl mx-auto p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search games..."
              className="w-full pl-11 pr-4 py-2.5 border-2 border-gray-200 dark:border-dark-border rounded-xl transition-colors focus:outline-none focus:border-gray-300 dark:focus:border-gray-600 dark:bg-dark-bg dark:text-dark-text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        <button 
          onClick={() => setShowAddGameModal(true)}
          className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95 flex items-center gap-2"
          style={{ background: FILL_GRADIENT }}
        >
          <Plus className="w-5 h-5" />
          Add Game
        </button>
      </div>

      {isLoadingProjects ? (
        <SmoothLoadingSpinner 
          isLoading={true} 
          size="lg" 
          message="Loading projects..." 
          className="py-12" 
        />
      ) : searchedProjects.length === 0 ? (
        <div className="text-center py-16">
          <Gamepad2 className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2">
            {searchQuery ? 'No games found' : 'No games yet'}
          </h3>
          <p className="text-gray-500 dark:text-dark-text-muted">
            {searchQuery 
              ? `No games match "${searchQuery}"`
              : `Create your first game in this ${isPersonalWorkspace ? 'workspace' : 'team'}`
            }
          </p>
          {!searchQuery && (
            <button 
              onClick={() => setShowAddGameModal(true)}
              className="mt-6 px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95 flex items-center gap-2 mx-auto"
              style={{ background: FILL_GRADIENT }}
            >
              <Plus className="w-5 h-5" />
              Add Game
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {searchedProjects.map((project, index) => {
            const versionText = project.stats?.versionCount 
              ? `${project.stats.versionCount} version${project.stats.versionCount > 1 ? 's' : ''}` 
              : 'No versions'
            
            return (
              <div
                key={`project-${project.id}-${index}`}
                className="group bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl p-6 hover:shadow-lg transition-all duration-200 cursor-pointer"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    {/* Project Icon */}
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-xl flex items-center justify-center overflow-hidden p-3">
                      {project.content?.icon_url ? (
                        <img 
                          src={project.content.icon_url} 
                          alt={`${project.name} icon`}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                            const parent = e.currentTarget.parentElement
                            if (parent) {
                              parent.innerHTML = '<svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 01-.657.643 48.39 48.39 0 01-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 01-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 00-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 01-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 00.657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 005.427-.63 48.05 48.05 0 00.582-4.717.532.532 0 00-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 00.658-.663 48.422 48.422 0 00-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 01-.61-.58v0z" /></svg>'
                            }
                          }}
                        />
                      ) : (
                        <Gamepad2 className="w-8 h-8 text-gray-400" />
                      )}
                    </div>

                    {/* Project Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-dark-text truncate">
                          {project.name}
                        </h3>
                        <StatusBadge status={project.status} size="sm" />
                      </div>
                      
                      <p className="text-sm text-gray-500 dark:text-dark-text-muted mb-3 line-clamp-2">
                        {project.description || 'No description available'}
                      </p>
                      
                      {/* Project Stats */}
                      <div className="flex items-center gap-5 text-sm text-gray-500 dark:text-dark-text-muted">
                        <div className="flex items-center gap-1.5">
                          <GitBranch className="w-4 h-4" />
                          <span className="font-medium">{versionText}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" />
                          <span className="font-medium">{formatTimeAgo(project.updated_at || project.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Area */}
                  <div className="flex items-center">
                    <button 
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                      onClick={(e) => {
                        e.stopPropagation()
                        console.log('Project actions clicked for:', project.name)
                      }}
                      title="More actions"
                    >
                      <MoreHorizontal className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'projects':
        return renderProjectsTab()
      case 'members':
        return (
          <div className="max-w-7xl mx-auto p-8">
            {currentUser ? (
              <TeamMembersList
                teamId={teamId!}
                currentUserRole={isPersonalWorkspace ? 'owner' : (team as any).role || 'viewer'}
                currentUserId={currentUser.user?.id || currentUser.id}
                onError={(message) => {
                  console.error('Member management error:', message)
                  // Show toast notification for member management errors
                  if (message.includes('already a team member')) {
                    toast.error('This user is already a member of the team')
                  } else if (message.includes('not found')) {
                    toast.error('User not found')
                  } else if (message.includes('permission')) {
                    toast.error('You don\'t have permission to perform this action')
                  } else {
                    toast.error(message)
                  }
                }}
                onSuccess={(message) => {
                  console.log('Member management success:', message)
                  // Toast notifications are already handled in TeamMembersList component
                }}
              />
            ) : (
              <SmoothLoadingSpinner 
                isLoading={true} 
                size="lg" 
                message="Loading user data..." 
                className="py-12" 
              />
            )}
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {renderHeader()}
      {renderTabs()}
      {renderTabContent()}
      
      {/* Add Game Modal */}
      <AddGameModal
        isOpen={showAddGameModal}
        onClose={() => setShowAddGameModal(false)}
        targetTeamId={isPersonalWorkspace ? 'personal' : (teamId || '')}
        targetTeamName={isPersonalWorkspace ? 'Personal Workspace' : (team?.name || '')}
        onGameAdded={() => {
          refreshProjects()
          if (isPersonalWorkspace) {
            refreshPersonalWorkspace()
          } else {
            refreshTeams()
          }
        }}
      />

      {/* Edit Team Modal */}
      {!isPersonalWorkspace && (
        <TeamEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          team={team as any}
          onSave={handleEditTeamSave}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative bg-white dark:bg-dark-surface rounded-2xl shadow-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-dark-text mb-3">
              Delete Team
            </h3>
            <p className="text-gray-500 dark:text-dark-text-muted mb-6">
              Are you sure you want to delete "{team.name}"? This action cannot be undone and will remove all team data.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-6 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTeam}
                disabled={isDeleting}
                className="px-6 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95 flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Team'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Click outside to close actions menu */}
      {showActionsMenu && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setShowActionsMenu(false)}
        />
      )}
    </div>
  )
}

export default TeamDetailPage